// IA Bundle builders — <ia-routine-bundle> (one pick per dropdown step) and
// <ia-custom-bundle> (qty steppers gated by a required item count).
//
// Both add ONLY the picked child products in ONE /cart/add.js call
// (window.iaCartAPI.addToCartJson supports the multi-item body) — the parent
// product is never a cart line (v2 contract): the bundle app's cart-transform
// materializes it as the merged line. Every child line carries the SAME
// properties (the app rejects mixed groups):
//   _bundle_id     = "<parentProductId>-<timestamp>"
//   _bundle_parent = parent VARIANT id
//   _bundle_title  = parent product title (merged cart line's title)
//   _bundle_step   = step heading (routine children only)
// No count property — the app computes the real count server-side and
// enforces size limits from the parent's metafields.
// All markup is server-rendered; the components only toggle attributes and
// recompute totals — no element creation.
import { EVENTS, publish, subscribe } from 'ia-util-pubsub'
import { openCart } from 'ia-util-helpers'

class IaBundleBase extends HTMLElement {
  connectedCallback() {
    this.atcButton = this.querySelector('[data-bundle-atc]')
    this.totalEl = this.querySelector('[data-bundle-total]')
    this.compareEl = this.querySelector('[data-bundle-compare]')
    this.dividerEl = this.querySelector('[data-bundle-divider]')
    this.errorEl = this.querySelector('[data-bundle-error]')
    this.errorDefault = this.errorEl?.textContent.trim()
    this.canSubmit = !this.atcButton?.disabled
    this.atcButton?.addEventListener('click', this.handleAtc.bind(this))
    // addToCartJson resolves null on failure; the server's sentence travels on this event.
    this.unsubscribeCartError?.()
    this.unsubscribeCartError = subscribe(EVENTS.cartError, (event) => {
      if (this.busy) this.lastError = event.detail?.error
    })
  }

  disconnectedCallback() {
    this.unsubscribeCartError?.()
  }

  get maxQty() {
    return parseInt(this.dataset.maxQty) || Infinity
  }

  // Units of a variant already in the bag, across its lines.
  heldQty(variantId) {
    return (window.iaCartAPI?.cart?.items || []).reduce(
      (total, item) => (item.variant_id === variantId ? total + item.quantity : total),
      0
    )
  }

  // Would any picked variant pass the per-product cap once added?
  exceedsCap(items) {
    const picked = new Map()
    items.forEach(({ id, quantity }) => picked.set(id, (picked.get(id) || 0) + quantity))
    return [...picked].some(([id, quantity]) => this.heldQty(id) + quantity > this.maxQty)
  }

  formatMoney(cents) {
    if (window.iaCartAPI) return window.iaCartAPI.formatPrice(cents)
    return `$${(cents / 100).toFixed(2)}`
  }

  // Owner block id (wrapper id minus the prefix): the payment modal keys its bundle sync on it.
  get blockId() {
    return this.closest('[id^="shopify-block-"]')?.id.replace('shopify-block-', '')
  }

  // Display-only bundle discount (data-discount, percent) until the app ships.
  get discountPercent() {
    const discount = parseInt(this.dataset.discount)
    if (Number.isNaN(discount)) return 0
    return Math.min(Math.max(discount, 0), 80)
  }

  renderTotal(cents, compareCents = 0) {
    const compare = Math.max(compareCents, cents)
    const price = Math.round((cents * (100 - this.discountPercent)) / 100)
    const show = cents > 0
    // Kept on the element for a consumer that connects after this ran (ia-pdp-loyalty).
    this.dataset.total = price
    publish(EVENTS.bundleChange, { detail: { blockId: this.blockId, total: price, compare } })
    if (!this.totalEl) return
    this.totalEl.hidden = !show
    if (this.dividerEl) this.dividerEl.hidden = !show
    this.totalEl.textContent = this.formatMoney(price)
    if (this.compareEl) {
      const showCompare = show && compare > price
      this.compareEl.hidden = !showCompare
      if (showCompare) this.compareEl.textContent = this.formatMoney(compare)
    }
  }

  setCanSubmit(value) {
    this.canSubmit = value
    if (!this.atcButton || this.busy) return
    this.atcButton.disabled = !value
    this.atcButton.setAttribute('aria-disabled', String(!value))
  }

  setBusy(value) {
    this.busy = value
    this.atcButton?.classList.toggle('adding', value)
    if (this.atcButton) this.atcButton.disabled = value || !this.canSubmit
  }

  // `show`: false hides, true shows the generic text, a string shows that text.
  showError(show) {
    if (!this.errorEl) return
    this.errorEl.classList.toggle('ia-hidden', !show)
    if (!show) return
    const target = this.errorEl.firstElementChild || this.errorEl
    target.textContent = typeof show === 'string' ? show : this.errorDefault
  }

  // Subclasses return [{ id, quantity, step? }, ...] or null when incomplete.
  collectChildren() {
    return null
  }

  // Clear the picks after a successful add; subclasses opt in.
  reset() {}

  async handleAtc() {
    if (!this.canSubmit || this.busy) return
    const children = this.collectChildren()
    if (!children?.length) return

    this.showError(false)
    // Past the cap the tap gets the standard note, no request (same as ia-product-buy).
    if (this.exceedsCap(children)) {
      this.showError(this.dataset.limitMessage || true)
      return
    }
    this.setBusy(true)
    this.lastError = null

    const bundleId = `${this.dataset.parentId}-${Date.now()}`
    // Identical on every line of the add — the app rejects mixed groups.
    const shared = {
      _bundle_id: bundleId,
      _bundle_parent: this.dataset.parentVariant || '',
      _bundle_title: this.dataset.parentTitle || ''
    }
    const items = children.map((child) => ({
      id: child.id,
      quantity: child.quantity,
      properties: {
        ...shared,
        ...(child.step ? { _bundle_step: child.step } : {})
      }
    }))

    try {
      // addToCartJson traps fetch errors internally and resolves null.
      const cart = await window.iaCartAPI?.addToCartJson({ items })
      if (!cart) throw new Error('Bundle add failed')
      this.reset()
      openCart()
    } catch (error) {
      // The server's own sentence (a refused cap, stock) beats the generic line.
      this.showError(this.lastError?.message || true)
    } finally {
      this.setBusy(false)
    }
  }
}

class IaRoutineBundle extends IaBundleBase {
  connectedCallback() {
    super.connectedCallback()
    this.steps = Array.from(this.querySelectorAll('[data-step]'))
    this.variantsCache = new WeakMap()

    this.addEventListener('click', this.handleClick.bind(this))
    this.addEventListener('change', this.handleChange.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))
    this.onOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener('click', this.onOutsideClick)

    this.updateState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('click', this.onOutsideClick)
  }

  handleClick(event) {
    const trigger = event.target.closest('.ia-bundle-select__trigger')
    if (!trigger || !this.contains(trigger)) return
    const expanded = trigger.getAttribute('aria-expanded') === 'true'
    this.closePanels()
    if (!expanded) this.openPanel(trigger)
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) this.closePanels()
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return
    const open = this.querySelector('.ia-bundle-select__trigger[aria-expanded="true"]')
    if (!open) return
    this.closePanels()
    open.focus()
  }

  openPanel(trigger) {
    trigger.setAttribute('aria-expanded', 'true')
    const panel = document.getElementById(trigger.getAttribute('aria-controls'))
    if (panel) panel.hidden = false
  }

  closePanels() {
    this.querySelectorAll('.ia-bundle-select__trigger[aria-expanded="true"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false')
      const panel = document.getElementById(trigger.getAttribute('aria-controls'))
      if (panel) panel.hidden = true
    })
  }

  handleChange(event) {
    const input = event.target
    if (input.matches('[data-product-radio]')) {
      const step = input.closest('[data-step]')
      this.syncStepProduct(step, input.value)
      this.closePanels()
      step.querySelector('.ia-bundle-select__trigger')?.focus()
      this.updateState()
    } else if (input.matches('[data-bundle-chip]')) {
      const step = input.closest('[data-step]')
      this.syncStepSizes(step, input)
      this.renderStepImages(step)
      this.updateState()
    }
  }

  // Reveal the chosen product's trigger row + size-chip zone, hide the rest.
  syncStepProduct(step, productId) {
    step.querySelectorAll('[data-current-for]').forEach((el) => {
      el.hidden = el.dataset.currentFor !== productId
    })
    step.querySelectorAll('[data-sizes-for]').forEach((el) => {
      el.hidden = el.dataset.sizesFor !== productId
    })
  }

  selectedProductId(step) {
    // Single-product steps render no radios — the step carries the id itself.
    return (
      step.querySelector('[data-product-radio]:checked')?.value ||
      step.dataset.selectedProduct ||
      null
    )
  }

  variantsFor(step, productId) {
    let byProduct = this.variantsCache.get(step)
    if (!byProduct) {
      byProduct = {}
      this.variantsCache.set(step, byProduct)
    }
    if (!byProduct[productId]) {
      const script = step.querySelector(`script[data-bundle-variants="${productId}"]`)
      try {
        byProduct[productId] = script ? JSON.parse(script.textContent) : []
      } catch (error) {
        byProduct[productId] = []
      }
    }
    return byProduct[productId]
  }

  // The variant a product's own chips point at, available or not.
  matchVariant(step, productId) {
    const variants = this.variantsFor(step, productId)
    if (!variants.length) return null

    const sizesZone = step.querySelector(`[data-sizes-for="${productId}"]`)
    if (!sizesZone) {
      return variants.find((variant) => variant.available) || null
    }
    const picks = Array.from(sizesZone.querySelectorAll('[data-bundle-chip]:checked')).map(
      (chip) => [Number(chip.dataset.optionIndex), chip.value]
    )
    return (
      variants.find((variant) => picks.every(([index, value]) => variant.options[index] === value)) ||
      null
    )
  }

  resolveVariant(step) {
    const productId = this.selectedProductId(step)
    if (!productId) return null
    const match = this.matchVariant(step, productId)
    return match?.available ? match : null
  }

  // Products in a step share option values, so one chip drives them all.
  syncStepSizes(step, chip) {
    const { optionIndex } = chip.dataset

    step.querySelectorAll('[data-sizes-for]').forEach((zone) => {
      if (zone.contains(chip)) return
      const twin = Array.from(zone.querySelectorAll('[data-bundle-chip]')).find(
        (candidate) =>
          candidate.dataset.optionIndex === optionIndex &&
          candidate.value === chip.value &&
          !candidate.disabled
      )
      if (twin) twin.checked = true
    })
  }

  // Point every option row in the step at its product's current variant image.
  renderStepImages(step) {
    step.querySelectorAll('[data-variant-images]').forEach((media) => {
      const variant = this.matchVariant(step, media.dataset.variantImages)
      if (!variant) return
      media.querySelectorAll('[data-variant-image]').forEach((frame) => {
        frame.hidden = frame.dataset.variantImage !== String(variant.id)
      })
    })
  }

  updateState() {
    let total = 0
    let compare = 0
    let complete = this.steps.length > 0
    this.steps.forEach((step) => {
      const variant = this.resolveVariant(step)
      if (variant) {
        total += variant.price
        compare += Math.max(variant.compare_at_price || 0, variant.price)
      } else {
        complete = false
      }
    })
    this.renderTotal(total, compare)
    this.setCanSubmit(complete)
  }

  collectChildren() {
    const children = []
    for (const step of this.steps) {
      const variant = this.resolveVariant(step)
      if (!variant) return null
      children.push({ id: variant.id, quantity: 1, step: step.dataset.stepLabel || undefined })
    }
    return children
  }
}

class IaCustomBundle extends IaBundleBase {
  connectedCallback() {
    super.connectedCallback()
    this.required = parseInt(this.dataset.required) || 0
    this.counterEl = this.querySelector('[data-bundle-counter]')
    this.labelEl = this.querySelector('[data-bundle-label]')
    this.rows = Array.from(this.querySelectorAll('[data-bundle-row]'))
    this.unsubscribeQty = subscribe(EVENTS.qtyChange, this.handleQtyChange.bind(this))
    this.updateState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.unsubscribeQty?.()
  }

  handleQtyChange(event) {
    const { id } = event.detail
    if (!id || !this.querySelector(`ia-product-qty[data-id="${id}"]`)) return
    this.updateState()
  }

  rowQty(row) {
    return parseInt(row.querySelector('ia-product-qty')?.value) || 0
  }

  updateState() {
    let count = 0
    let price = 0
    let compare = 0
    this.rows.forEach((row) => {
      const qty = this.rowQty(row)
      const rowPrice = parseInt(row.dataset.price) || 0
      count += qty
      price += qty * rowPrice
      compare += qty * Math.max(parseInt(row.dataset.compare) || 0, rowPrice)
    })

    // Cap every stepper so the combined total can never pass the target.
    const remaining = Math.max(0, this.required - count)
    this.rows.forEach((row) => {
      row.querySelector('ia-product-qty')?.setAttribute('max', this.rowQty(row) + remaining)
    })

    if (this.counterEl) {
      const format = this.counterEl.dataset.counterFormat || '{count}/{required} Selected'
      this.counterEl.textContent = format
        .replace('{count}', count)
        .replace('{required}', this.required)
    }
    if (this.labelEl) {
      const remaining = Math.max(0, this.required - count)
      this.labelEl.textContent = remaining > 0
        ? `${this.labelEl.dataset.labelSelect || 'Select'} ${remaining}`
        : this.labelEl.dataset.labelAdd || 'Add to bag'
    }
    // Uniform pricing: the set price is fixed — keep it pinned regardless of
    // how many items are picked (data-upfront-* from the server).
    const upfront = parseInt(this.dataset.upfrontPrice)
    if (Number.isNaN(upfront)) {
      this.renderTotal(price, compare)
    } else {
      this.renderTotal(upfront, parseInt(this.dataset.upfrontCompare) || 0)
    }
    this.setCanSubmit(count === this.required && this.required > 0)
  }

  collectChildren() {
    const children = this.rows
      .filter((row) => this.rowQty(row) > 0)
      .map((row) => ({ id: Number(row.dataset.variantId), quantity: this.rowQty(row) }))
    return children.length ? children : null
  }

  // Back to an empty builder: every stepper to 0, then re-derive counter/label/total.
  reset() {
    this.rows.forEach((row) => row.querySelector('ia-product-qty')?.reset())
    this.updateState()
  }
}

if (!customElements.get('ia-routine-bundle')) {
  customElements.define('ia-routine-bundle', IaRoutineBundle)
}
if (!customElements.get('ia-custom-bundle')) {
  customElements.define('ia-custom-bundle', IaCustomBundle)
}

export { IaBundleBase }
